import { ArrowSquareOut } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import { INTEGRATIONS_MAP } from "../constants";

import type { FunctionComponent, JSX, SVGProps } from "react";

type IntegrationItemProps = {
  type: keyof typeof INTEGRATIONS_MAP;
  description: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
} & (
  | {
      redirectUrl: string;
      settingsComponent?: never;
      isLoading?: boolean;
      hasError?: never;
    }
  | {
      redirectUrl?: never;
      settingsComponent: JSX.Element;
      isLoading?: never;
      hasError?: boolean;
    }
  | {
      redirectUrl?: never;
      settingsComponent?: never;
      isLoading: boolean;
      hasError?: never;
    }
);

export default function IntegrationItem({
  type,
  description,
  icon: Icon,
  redirectUrl,
  settingsComponent,
  hasError,
}: IntegrationItemProps) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { title } = INTEGRATIONS_MAP[type];
  let topRightComponent = <Skeleton className="size-8 rounded-full" />;

  if (redirectUrl) {
    topRightComponent =
      type === "slack" ? (
        <Button
          intent="iconOnly"
          variant="secondary-black"
          size="sm"
          onClick={() =>
            window.open(
              `${redirectUrl}${workspaceId}_${Math.floor(Date.now() / Math.random())}`,
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <ArrowSquareOut />
        </Button>
      ) : (
        <Button asChild intent="iconOnly" variant="secondary-black" size="sm">
          <Link to={redirectUrl} target="_blank">
            <ArrowSquareOut />
          </Link>
        </Button>
      );
  } else if (settingsComponent) {
    topRightComponent = settingsComponent;
  }

  return (
    <div
      className={clsx(
        "flex flex-col gap-y-2 rounded-3xl border p-2",
        hasError ? "border-red-400 bg-red-50" : "border-black-200 bg-white",
      )}
    >
      <div className="flex justify-between gap-2">
        {/* Integration icon */}
        <Icon />
        {topRightComponent}
      </div>

      {/* Title */}
      <h3 className="text-xl-bold">{title}</h3>

      {/* Description */}
      <p className="text-black-600">{description}</p>
    </div>
  );
}
